import { useCallback, useEffect, useMemo, useRef, useState, type PropsWithChildren } from 'react'
import type { AppTheme, CanvasBackgroundKey, ClientUserPreferences, FocusSoundscapeKey, OrbitSkinKey } from '../types/schema'
import { useClientPreferences } from './useClientPreferences'
import {
  BACKGROUND_COSMETIC_TO_KEY,
  ORBIT_SKIN_COSMETIC_TO_KEY,
  SOUNDSCAPE_COSMETIC_TO_KEY,
  THEME_COSMETIC_TO_KEY,
  type OrbitSkinCosmeticId,
} from '../assets/cosmetics'
import {
  PreviewContext,
  type ActivePreview,
  type PreviewContextValue,
  type PreviewKind,
  type StartPreviewInput,
} from './previewContextCore'

const DEFAULT_DURATION_MS = 10_000
const SENTINEL_KEY = 'timelining-preview-snapshot'

type PrefField = 'theme' | 'canvasBackground' | 'focusSoundscape' | 'orbitSkin'

interface PreviewSentinel {
  field: PrefField
  originalValue?: string
}

const resolvePreviewTarget = (kind: PreviewKind, cosmeticId: string): { field: PrefField; value: string } | null => {
  if (kind === 'THEME') {
    const value = THEME_COSMETIC_TO_KEY[cosmeticId as keyof typeof THEME_COSMETIC_TO_KEY]
    return value ? { field: 'theme', value } : null
  }
  if (kind === 'BACKGROUND') {
    const value = BACKGROUND_COSMETIC_TO_KEY[cosmeticId]
    return value ? { field: 'canvasBackground', value } : null
  }
  if (kind === 'SOUNDSCAPE') {
    const value = SOUNDSCAPE_COSMETIC_TO_KEY[cosmeticId]
    return value ? { field: 'focusSoundscape', value } : null
  }
  if (kind === 'ORBIT') {
    const value = ORBIT_SKIN_COSMETIC_TO_KEY[cosmeticId as OrbitSkinCosmeticId]
    return value ? { field: 'orbitSkin', value } : null
  }
  return null
}

const buildPatch = (field: PrefField, value?: string): Partial<ClientUserPreferences> => {
  switch (field) {
    case 'theme':
      return { theme: (value as AppTheme) || 'system' }
    case 'canvasBackground':
      return { canvasBackground: value as CanvasBackgroundKey | undefined }
    case 'focusSoundscape':
      return { focusSoundscape: value as FocusSoundscapeKey | undefined }
    case 'orbitSkin':
      return { orbitSkin: value as OrbitSkinKey | undefined }
  }
}

const readSentinel = (): PreviewSentinel | null => {
  try {
    const raw = window.localStorage.getItem(SENTINEL_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (parsed && typeof parsed === 'object' && typeof parsed.field === 'string' && (typeof parsed.originalValue === 'string' || parsed.originalValue === undefined)) {
      return parsed as PreviewSentinel
    }
    return null
  } catch {
    return null
  }
}

const writeSentinel = (sentinel: PreviewSentinel) => {
  try {
    window.localStorage.setItem(SENTINEL_KEY, JSON.stringify(sentinel))
  } catch {
    // ignore — worst case a crash mid-preview requires a manual theme reselect
  }
}

const clearSentinel = () => {
  try {
    window.localStorage.removeItem(SENTINEL_KEY)
  } catch {
    // ignore
  }
}

export const PreviewProvider = ({ children }: PropsWithChildren) => {
  const { preferences, updatePreferences } = useClientPreferences()
  const [activePreview, setActivePreview] = useState<ActivePreview | null>(null)
  const [remainingMs, setRemainingMs] = useState(0)
  const [previewAvatarId, setPreviewAvatarId] = useState<string | null>(null)

  const preferencesRef = useRef(preferences)
  useEffect(() => {
    preferencesRef.current = preferences
  }, [preferences])

  const prefSnapshotRef = useRef<{ field: PrefField; originalValue?: string; previewedValue: string } | null>(null)
  const avatarActiveRef = useRef(false)
  const timeoutRef = useRef<number | null>(null)
  const intervalRef = useRef<number | null>(null)

  const clearTimers = useCallback(() => {
    if (timeoutRef.current !== null) {
      window.clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
    if (intervalRef.current !== null) {
      window.clearInterval(intervalRef.current)
      intervalRef.current = null
    }
  }, [])

  const revert = useCallback(() => {
    clearTimers()
    if (avatarActiveRef.current) {
      avatarActiveRef.current = false
      setPreviewAvatarId(null)
    }
    const snapshot = prefSnapshotRef.current
    if (snapshot && preferencesRef.current[snapshot.field] === snapshot.previewedValue) {
      updatePreferences(buildPatch(snapshot.field, snapshot.originalValue))
    }
    prefSnapshotRef.current = null
    clearSentinel()
    setActivePreview(null)
    setRemainingMs(0)
  }, [clearTimers, updatePreferences])

  const revertRef = useRef(revert)
  useEffect(() => {
    revertRef.current = revert
  }, [revert])

  const startPreview = useCallback((input: StartPreviewInput) => {
    if (activePreview) return
    const durationMs = input.durationMs ?? DEFAULT_DURATION_MS

    if (input.kind === 'AVATAR') {
      avatarActiveRef.current = true
      setPreviewAvatarId(input.cosmeticId)
    } else {
      const target = resolvePreviewTarget(input.kind, input.cosmeticId)
      if (!target) return
      const originalValue = preferencesRef.current[target.field]
      if (originalValue === target.value) return
      prefSnapshotRef.current = { field: target.field, originalValue, previewedValue: target.value }
      writeSentinel({ field: target.field, originalValue })
      updatePreferences(buildPatch(target.field, target.value))
    }

    setActivePreview({
      kind: input.kind,
      cosmeticId: input.cosmeticId,
      label: input.label,
      startedAt: Date.now(),
      durationMs,
    })
    setRemainingMs(durationMs)

    timeoutRef.current = window.setTimeout(() => revertRef.current(), durationMs)
    intervalRef.current = window.setInterval(() => {
      setRemainingMs((prev) => Math.max(0, prev - 1000))
    }, 1000)
  }, [activePreview, updatePreferences])

  const cancelPreview = useCallback(() => {
    revertRef.current()
  }, [])

  useEffect(() => {
    const sentinel = readSentinel()
    if (sentinel) {
      updatePreferences(buildPatch(sentinel.field, sentinel.originalValue))
      clearSentinel()
    }
    // Crash-recovery pass runs once on boot, before any preview can start.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => clearTimers, [clearTimers])

  const value: PreviewContextValue = useMemo(() => ({
    activePreview,
    remainingMs,
    previewAvatarId,
    startPreview,
    cancelPreview,
  }), [activePreview, remainingMs, previewAvatarId, startPreview, cancelPreview])

  return <PreviewContext.Provider value={value}>{children}</PreviewContext.Provider>
}
